import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Host,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    SkipSelf,
    ViewEncapsulation,
} from '@angular/core';
import {
    AbstractControl,
    ControlContainer,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    FormGroupDirective,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgForm,
    UntypedFormBuilder,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { SrpValidators } from '@salesrun/ngx-validation';
import { overrideFunction, OverridenFunctionHandle } from '@salesrun/override';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'srp-password-setup',
    templateUrl: './password-setup.component.html',
    styleUrls: ['./password-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { '[class.srp-password-setup]': 'true' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PasswordSetupComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PasswordSetupComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class PasswordSetupComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {

    private _destroy$: Subject<boolean> = new Subject();
    private _control: AbstractControl;
    private _markAsTouchedRevert: OverridenFunctionHandle;
    innerValue: string = null;
    form: FormGroup;
    confirmPasswordErrorStateMatcher = new ConfirmPasswordErrorStateMatcher(this._errorStateMatcher);

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChange = (_: string) => { };
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onTouched = () => { };

    @Input()
    get value(): string | null {
        return this.innerValue === ' ' ? null : this.innerValue;
    }

    set value(value: string | null) {
        this.innerValue = value;
        this._cdr.markForCheck();
    }

    @Input() formControlName: string;
    @Input() isNewPassword: boolean = true;

    get passwordPalceHolder(): string {
        return (this.isNewPassword ? 'New ' : '') + 'Password';
    }

    get passwordRequiredErrorLabel(): string {
        return (this.isNewPassword ? 'New ' : '') + 'Password is required.';
    }

    get passwordValidationErrorLabel(): string {
        return (this.isNewPassword ? 'New ' : '') + 'Password must contain minimum 6 Characters including at least 1 Letter, 1 Number and 1 Special Symbol.';
    }

    get confirmPlaceHolder(): string {
        return 'Confirm ' + (this.isNewPassword ? 'New ' : '') + 'Password';
    }

    get confirmPasswordRequiredErrorLabel(): string {
        return (this.isNewPassword ? 'New ' : '') + 'Password is required.';
    }

    get newPasswordControl(): AbstractControlTyped<string> {
        return this.form.controls.newPassword;
    }

    get confirmNewPasswordControl(): AbstractControlTyped<string> {
        return this.form.controls.confirmNewPassword;
    }

    constructor(
        private _cdr: ChangeDetectorRef,
        private _formBuilder: UntypedFormBuilder,
        private _errorStateMatcher: ErrorStateMatcher,
        @Optional() @Host() @SkipSelf() private controlContainer: ControlContainer,
    ) {
        this.form = this._formBuilder.group({
            newPassword: [{ value: null, disabled: false }, [
                SrpValidators.required,
                SrpValidators.password,
            ]],
            confirmNewPassword: [{ value: null, disabled: true }, [
                SrpValidators.required,
            ]],
        }, { validators: SrpValidators.fieldMatch('newPassword', 'confirmNewPassword') });
    }

    ngOnInit(): void {
        this._overrideControl();
        this.form.valueChanges
            .pipe(
                takeUntil(this._destroy$),
            ).subscribe((value) => {
                if (!value.newPassword) {
                    this.confirmNewPasswordControl.patchValue(null, { emitEvent: false });
                }
                if (this.newPasswordControl.valid) {
                    this.confirmNewPasswordControl.enable({ onlySelf: true, emitEvent: false });
                } else {
                    this.confirmNewPasswordControl.disable({ onlySelf: true, emitEvent: false });
                }
                this.onChange(value.newPassword);
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next(true);
        this._destroy$.complete();
    }

    writeValue(value: string | null): void {
        this.value = value;
    }

    registerOnChange(fn: (v: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    validate(control: AbstractControl): ValidationErrors {
        if (!this.newPasswordControl.valid) {
            return { passwordError: true };
        }
        if (!this.confirmNewPasswordControl.value || !this.confirmNewPasswordControl.valid) {
            return { passwordError: true };
        }
        if (!this.form.valid) {
            return { passwordError: true };
        }
        return null;
    }

    private _overrideControl(): void {
        if (!this.controlContainer || !this.formControlName) return;
        this._control = this.controlContainer.control.get(this.formControlName);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this._markAsTouchedRevert = overrideFunction(this._control, c => c.markAsTouched, (origFn, args) => {
            origFn(args);
            this.form.markAllAsTouched();
            this._cdr.markForCheck();
        });
    }
}

class ConfirmPasswordErrorStateMatcher implements ErrorStateMatcher {
    constructor(private _errorStateMatcher: ErrorStateMatcher) { }
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return this._errorStateMatcher.isErrorState(control, form) || control.parent?.hasError('nomatch');
    }
}